import { Component } from "@theme/component";
import { CartActions } from "@theme/cart-actions";
import { DialogOpenEvent } from "@theme/dialog";

/**
 * 4th of July cart drawer upsell — self-contained; safe to delete with
 * snippets/cart-drawer-july-special.liquid and its render in header-actions.
 *
 * @extends {Component}
 */
export class CartDrawerJulySpecial extends Component {
  /** @type {number} */
  qvIndex = 0;
  /** @type {((event: KeyboardEvent) => void) | null} */
  keydownHandler = null;
  /** @type {((event: Event) => void) | null} */
  trackScrollHandler = null;
  /** @type {((event: Event) => void) | null} */
  dialogOpenHandler = null;
  /** @type {Array<{ element: Element; type: string; handler: EventListener }>} */
  controlBindings = [];
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  scrollTimeout;

  constructor() {
    super();
    this.cartActions = new CartActions();
    this.handleRender = this.handleRender.bind(this);
    this.resetGallery = this.resetGallery.bind(this);
    this.stopGalleryEvent = this.stopGalleryEvent.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.initGallery();
    this.bindDialogOpen();
    document.addEventListener("cartDrawerAddon:render", this.handleRender);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("cartDrawerAddon:render", this.handleRender);
    this.unbindDialogOpen();
    this.teardownGallery();
  }

  handleRender() {
    this.unbindDialogOpen();
    this.teardownGallery();
    this.initGallery();
    this.bindDialogOpen();
  }

  /** @returns {HTMLElement|null} */
  getDialogComponent() {
    return this.querySelector("dialog-component");
  }

  /** @returns {HTMLDialogElement|null} */
  getDialog() {
    const dialog = this.querySelector("dialog-component dialog");
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** @returns {HTMLElement|null} */
  getGallery() {
    return this.querySelector(".cart-drawer-july-special__gallery");
  }

  /** @returns {HTMLElement|null} */
  getQvTrack() {
    return this.querySelector(".cart-drawer-july-special__qv-track");
  }

  /** @returns {NodeListOf<HTMLElement>} */
  getQvDots() {
    return this.querySelectorAll(".cart-drawer-july-special__qv-dots span");
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  getSlideWidth(track) {
    if (track.clientWidth > 0) return track.clientWidth;

    const gallery = track.closest(".cart-drawer-july-special__gallery");
    if (gallery instanceof HTMLElement && gallery.clientWidth > 0) {
      return gallery.clientWidth;
    }

    const firstSlide = track.children[0];
    if (firstSlide instanceof HTMLElement && firstSlide.offsetWidth > 0) {
      return firstSlide.offsetWidth;
    }

    return 0;
  }

  /**
   * @param {Event} event
   */
  stopGalleryEvent(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * @param {Element} element
   * @param {string} type
   * @param {EventListener} handler
   */
  bindControl(element, type, handler) {
    element.addEventListener(type, handler);
    this.controlBindings.push({ element, type, handler });
  }

  bindDialogOpen() {
    const dialogComponent = this.getDialogComponent();
    if (!dialogComponent || this.dialogOpenHandler) return;

    this.dialogOpenHandler = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.resetGallery();
        });
      });
    };

    dialogComponent.addEventListener(DialogOpenEvent.eventName, this.dialogOpenHandler);
  }

  unbindDialogOpen() {
    const dialogComponent = this.getDialogComponent();
    if (dialogComponent && this.dialogOpenHandler) {
      dialogComponent.removeEventListener(DialogOpenEvent.eventName, this.dialogOpenHandler);
    }
    this.dialogOpenHandler = null;
  }

  resetGallery() {
    const track = this.getQvTrack();
    if (!track) return;

    track.scrollLeft = 0;
    this.qvIndex = 0;
    this.setQuickViewIndex(0);
  }

  initGallery() {
    const track = this.getQvTrack();
    const gallery = this.getGallery();
    if (!track || !gallery || this.trackScrollHandler) return;

    this.trackScrollHandler = () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        const slideWidth = this.getSlideWidth(track);
        if (!slideWidth) return;
        const nextIndex = Math.round(track.scrollLeft / slideWidth);
        this.setQuickViewIndex(nextIndex);
      }, 70);
    };

    track.addEventListener("scroll", this.trackScrollHandler, { passive: true });

    const prev = gallery.querySelector(".cart-drawer-july-special__qv-prev");
    const next = gallery.querySelector(".cart-drawer-july-special__qv-next");

    const navEvent = window.matchMedia("(pointer: coarse)").matches ? "pointerup" : "click";

    if (prev instanceof HTMLElement) {
      const onPrev = (event) => {
        this.stopGalleryEvent(event);
        this.stepQuickViewBy(-1);
      };
      this.bindControl(prev, "pointerdown", this.stopGalleryEvent);
      this.bindControl(prev, navEvent, onPrev);
    }

    if (next instanceof HTMLElement) {
      const onNext = (event) => {
        this.stopGalleryEvent(event);
        this.stepQuickViewBy(1);
      };
      this.bindControl(next, "pointerdown", this.stopGalleryEvent);
      this.bindControl(next, navEvent, onNext);
    }

    gallery.querySelectorAll(".cart-drawer-july-special__qv-dots span").forEach((dot) => {
      if (!(dot instanceof HTMLElement)) return;

      const onDot = (event) => {
        this.stopGalleryEvent(event);
        const index = Number(dot.dataset.index ?? 0);
        if (!Number.isNaN(index)) {
          this.goQuickViewTo(index);
        }
      };

      this.bindControl(dot, "pointerdown", this.stopGalleryEvent);
      this.bindControl(dot, navEvent, onDot);
    });

    this.keydownHandler = (event) => {
      const dialog = this.getDialog();
      if (!dialog?.open) return;

      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.stepQuickViewBy(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.stepQuickViewBy(-1);
      }
    };

    document.addEventListener("keydown", this.keydownHandler);
  }

  teardownGallery() {
    const track = this.getQvTrack();
    if (track && this.trackScrollHandler) {
      track.removeEventListener("scroll", this.trackScrollHandler);
    }

    for (const { element, type, handler } of this.controlBindings) {
      element.removeEventListener(type, handler);
    }
    this.controlBindings = [];

    if (this.keydownHandler) {
      document.removeEventListener("keydown", this.keydownHandler);
    }

    this.trackScrollHandler = null;
    this.keydownHandler = null;
    clearTimeout(this.scrollTimeout);
  }

  /** @param {number} index */
  setQuickViewIndex(index) {
    const track = this.getQvTrack();
    const dots = this.getQvDots();
    const slideCount = track?.children.length ?? 0;

    if (!track || slideCount === 0) return;

    const normalized = ((index % slideCount) + slideCount) % slideCount;
    this.qvIndex = normalized;

    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle("active", dotIndex === normalized);
    });
  }

  /** @param {number} index */
  goQuickViewTo(index) {
    const track = this.getQvTrack();
    if (!track || track.children.length === 0) return;

    const slideWidth = this.getSlideWidth(track);
    if (!slideWidth) return;

    const normalized =
      ((index % track.children.length) + track.children.length) % track.children.length;

    const targetLeft = slideWidth * normalized;

    if (window.matchMedia("(max-width: 749px)").matches) {
      track.scrollLeft = targetLeft;
    } else {
      track.scrollTo({
        left: targetLeft,
        behavior: "smooth",
      });
    }

    this.setQuickViewIndex(normalized);
  }

  /** @param {number} step */
  stepQuickViewBy(step) {
    this.goQuickViewTo(this.qvIndex + step);
  }

  /** @param {Event} event */
  async addToCart(event) {
    event.preventDefault();

    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest("[data-variant-id]");
    if (!(button instanceof HTMLElement)) return;

    const variantId = Number(button.dataset?.variantId);
    let properties = {};

    if (button.dataset?.properties?.length) {
      const arrayProperties = button.dataset.properties.split(",");

      for (let property of arrayProperties) {
        let [key, value] = property.trim().split(":");
        key = key?.trim();
        value = value?.trim();

        if (key && value) {
          properties[key] = value;
        }
      }
    }

    if (!variantId) return;

    const dialogComponent = this.getDialogComponent();
    if (
      dialogComponent &&
      "closeDialog" in dialogComponent &&
      typeof dialogComponent.closeDialog === "function"
    ) {
      await dialogComponent.closeDialog();
    }

    this.cartActions.addItems([
      {
        id: variantId,
        quantity: 1,
        properties,
      },
    ]);
  }
}

if (!customElements.get("cart-drawer-july-special")) {
  customElements.define("cart-drawer-july-special", CartDrawerJulySpecial);
}
